import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

interface PermissionGateProps {
  children: React.ReactNode;
  permission?: string | string[];
  role?: string | string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  showFallback?: boolean;
}

/**
 * Componente que controla el acceso a elementos de la UI basado en permisos y roles
 *
 * @param permission - Permiso(s) requerido(s)
 * @param role - Rol(es) requerido(s)
 * @param requireAll - Si se requieren TODOS los permisos/roles (AND) o solo uno (OR)
 * @param fallback - Componente a mostrar si no tiene permisos
 * @param showFallback - Si mostrar el fallback o simplemente no renderizar nada
 * @param children - Contenido a mostrar si tiene permisos
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  role,
  requireAll = false,
  fallback = null,
  showFallback = false,
}) => {
  const { hasPermission, hasRole } = usePermissions();

  let hasAccess = true;

  // Verificar permisos
  if (permission) {
    if (Array.isArray(permission)) {
      if (requireAll) {
        // Requiere TODOS los permisos
        hasAccess = permission.every((p) => hasPermission(p));
      } else {
        // Requiere AL MENOS UNO de los permisos
        hasAccess = permission.some((p) => hasPermission(p));
      }
    } else {
      hasAccess = hasPermission(permission);
    }
  }

  // Verificar roles (solo si pasó la verificación de permisos)
  if (hasAccess && role) {
    if (Array.isArray(role)) {
      if (requireAll) {
        // Requiere TODOS los roles (poco común)
        hasAccess = role.every((r) => hasRole(r));
      } else {
        // Requiere AL MENOS UNO de los roles
        hasAccess = role.some((r) => hasRole(r));
      }
    } else {
      hasAccess = hasRole(role);
    }
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  if (showFallback && fallback) {
    return <>{fallback}</>;
  }

  return null;
};

export default PermissionGate;
